import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Edit2, Trash2, Save, X, Wrench, FileText } from 'lucide-react';

/**
 * Settings → Condition Scripts. CRUD UI for the reusable repair-phrase
 * library that powers the "Repairs Detailed as Follows" section of each
 * Detailed Quotation PDF. Phrases are grouped by category.
 */

const blank = () => ({ category: '', name: '', body: '', sort_order: 10, active: true });

export default function ConditionScriptsPage() {
  const { api } = useAuth();
  const [scripts, setScripts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');

  const load = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([
        api.get('/condition-scripts', { params: { active_only: false } }),
        api.get('/condition-scripts/categories'),
      ]);
      setScripts(Array.isArray(s.data) ? s.data : []);
      setCategories(Array.isArray(c.data) ? c.data : []);
    } catch { setScripts([]); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing) return;
    if (!editing.category || !editing.body) { alert('Category and body are required.'); return; }
    const { _id, ...payload } = editing;
    try {
      if (_id) await api.put(`/condition-scripts/${_id}`, payload);
      else await api.post('/condition-scripts', payload);
      setEditing(null); load();
    } catch (e) { alert(e.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (id) => {
    try { await api.delete(`/condition-scripts/${id}`); setConfirmDelete(null); load(); } catch {/* no-op */}
  };

  const visible = scripts.filter(s => !filterCategory || s.category === filterCategory);
  const grouped = visible.reduce((acc, s) => {
    const key = s.category || '(uncategorised)';
    if (!acc[key]) acc[key] = [];
    acc[key].push(s);
    return acc;
  }, {});

  return (
    <div data-testid="condition-scripts-page" className="space-y-5">
      <div className="bg-white border border-[#E2E2DF] rounded-md p-6 relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-[3px] bg-[#D35400]" />
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-md bg-gradient-to-br from-[#D35400]/15 to-[#D35400]/5 flex items-center justify-center ring-1 ring-[#D35400]/10">
              <Wrench size={22} className="text-[#D35400]" strokeWidth={2} />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-0.5">Settings</p>
              <h1 className="text-2xl font-extrabold text-slate-900 font-[Manrope] tracking-tight">Condition Scripts</h1>
              <p className="text-sm text-slate-500 mt-1">{scripts.length} phrase{scripts.length === 1 ? '' : 's'} across {Object.keys(grouped).length} categor{Object.keys(grouped).length === 1 ? 'y' : 'ies'} — used to compose <span className="font-mono-num">appraisal_condition</span> on each quote.</p>
            </div>
          </div>
          <button data-testid="new-condition-script-btn" onClick={() => setEditing(blank())} className="flex items-center gap-2 px-3.5 py-2 bg-[#D35400] text-white rounded-md text-sm font-semibold hover:bg-[#A04000]">
            <Plus size={14} strokeWidth={2.5} /> New Phrase
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="bg-white border border-[#E2E2DF] rounded-xl p-4 flex flex-wrap gap-3 items-center">
        <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Filter:</span>
        <select data-testid="filter-category" value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="px-3 py-1.5 border border-[#E2E2DF] rounded-md text-sm bg-white">
          <option value="">All categories</option>
          {categories.map(c => <option key={c}>{c}</option>)}
        </select>
      </div>

      {/* Grouped by category */}
      {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([category, list]) => (
        <div key={category} className="bg-white border border-[#E2E2DF] rounded-xl overflow-hidden">
          <div className="px-5 py-3 bg-slate-50 border-b border-[#E2E2DF] flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-800">{category}</h2>
            <span className="text-[11px] font-mono-num text-slate-500">{list.length} phrase{list.length === 1 ? '' : 's'}</span>
          </div>
          <div className="divide-y divide-[#F0EFEC]">
            {list.sort((a,b) => (a.sort_order || 0) - (b.sort_order || 0)).map(s => (
              <div key={s._id} data-testid={`condition-script-row-${s._id}`} className="p-5 hover:bg-slate-50/50 group">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2 flex-wrap">
                      {s.name || '(unnamed)'}
                      <span className="text-[10px] font-mono-num text-slate-400 font-normal">#{s.sort_order || 0}</span>
                      {s.roof_type && <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#A04000] bg-[#FFF7ED] border border-[#D35400]/30 rounded-full px-2 py-0.5">{s.roof_type}</span>}
                      {s.recommendation && <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">{s.recommendation}</span>}
                    </h3>
                  </div>
                  <div className="inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                    <button data-testid={`edit-condition-script-${s._id}`} onClick={() => setEditing({ ...s })} className="p-1.5 text-slate-400 hover:text-[#D35400]"><Edit2 size={14} /></button>
                    {confirmDelete === s._id ? (
                      <>
                        <button data-testid={`confirm-delete-condition-${s._id}`} onClick={() => remove(s._id)} className="px-2 py-0.5 bg-red-500 text-white rounded text-xs">Delete</button>
                        <button onClick={() => setConfirmDelete(null)} className="px-2 py-0.5 border border-slate-300 rounded text-xs text-slate-500">Cancel</button>
                      </>
                    ) : (
                      <button data-testid={`delete-condition-${s._id}`} onClick={() => setConfirmDelete(s._id)} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
                    )}
                  </div>
                </div>
                <pre className="text-xs text-slate-600 whitespace-pre-wrap font-sans bg-slate-50/60 border border-[#E2E2DF] rounded-md p-2.5 leading-relaxed">{s.body}</pre>
              </div>
            ))}
          </div>
        </div>
      ))}

      {Object.keys(grouped).length === 0 && (
        <div className="bg-white border border-[#E2E2DF] rounded-xl p-12 text-center">
          <FileText size={28} className="mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">No condition phrases yet. Click "New Phrase" to start your library.</p>
        </div>
      )}

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div data-testid="condition-script-editor" className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h2 className="text-base font-bold text-[#0F172A]">{editing._id ? 'Edit phrase' : 'New phrase'}</h2>
              <button onClick={() => setEditing(null)} className="p-1.5 hover:bg-slate-100 rounded-md text-slate-500"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto flex-1">
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Category *</label>
                <input
                  data-testid="condition-category"
                  list="condition-category-list"
                  value={editing.category}
                  onChange={e => setEditing({ ...editing, category: e.target.value })}
                  placeholder="e.g. Re-bedding, Re-pointing, Flashings…"
                  className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm focus:outline-none focus:border-[#D35400]"
                />
                <datalist id="condition-category-list">
                  {categories.map(c => <option key={c} value={c} />)}
                </datalist>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="text-xs font-medium text-slate-600 block mb-1">Phrase name *</label>
                  <input data-testid="condition-name" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} placeholder="e.g. Standard re-bedding (concrete)" className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Sort order</label>
                  <input data-testid="condition-sort" type="number" value={editing.sort_order} onChange={e => setEditing({ ...editing, sort_order: parseInt(e.target.value, 10) || 0 })} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm font-mono-num" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Roof type <span className="text-slate-400 font-normal">(filter facet)</span></label>
                  <input data-testid="condition-roof-type" list="condition-roof-list" value={editing.roof_type || ''} onChange={e => setEditing({ ...editing, roof_type: e.target.value })} placeholder="e.g. Concrete, Longrun, Clay…" className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm" />
                  <datalist id="condition-roof-list">
                    {['Concrete', 'Old Concrete', 'Concrete & Old Concrete', 'Longrun', 'Iron', 'Clay', 'Decramastic', 'Gutter'].map(r => <option key={r} value={r} />)}
                  </datalist>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Recommendation</label>
                  <input data-testid="condition-recommendation" value={editing.recommendation || ''} onChange={e => setEditing({ ...editing, recommendation: e.target.value })} placeholder="Optional badge text" className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Phrase body *</label>
                <textarea data-testid="condition-body" value={editing.body} onChange={e => setEditing({ ...editing, body: e.target.value })} rows={8} placeholder="The repair phrase as it should appear on the customer quote. Plain text — line breaks are preserved." className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm leading-relaxed" />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={editing.active !== false} onChange={e => setEditing({ ...editing, active: e.target.checked })} className="accent-[#D35400] w-4 h-4" />
                Active (shown in the appraisal picker)
              </label>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-slate-200 bg-slate-50/50">
              <button onClick={() => setEditing(null)} className="px-4 py-2 border border-[#E2E2DF] rounded-lg text-sm text-slate-600 hover:bg-white font-medium">Cancel</button>
              <button data-testid="save-condition-script-btn" onClick={save} className="px-4 py-2 bg-[#D35400] text-white rounded-lg text-sm font-semibold hover:bg-[#A04000] flex items-center gap-1.5"><Save size={14} /> Save phrase</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
